/* eslint-disable camelcase */
import { FC, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { coreService, platform } from 'network-utils';
import { useSelector, useDispatch } from 'react-redux';
import { translate } from 'translations';
import { selectToken, selectUserUuid } from '@redux/slices/userSlice';
import { showAlert } from '@redux/slices/informationSlice';
import { useGetAccountData, getAccountData } from '@queries/platform/accountSettings';
import { getPrimaryCourseData, getAssignedContentData, getPrimaryCourseMetadata } from '@queries/platform/userContent';
import { getHomepageCompletionData } from '@queries/platform/homepage';
import { getLearnData } from '@queries/core/learn';
import SelectPrimaryCourse from './selectPrimaryCourse';
import { useAssignedContent } from '../../../queries/core/homepage';

const SelectPrimaryCourseContainer: FC<SelectPrimaryCourseContainerProps> = (
    props: SelectPrimaryCourseContainerProps
) => {
    const { setShowSuccessNotification, setNotificationMessage, shouldSubmitChange, onLoad, onSelect, onError } = props;
    const services = coreService.assignedContentsApi;
    const [coursesList, setCoursesList] = useState<{ displayName: string; uuid: string; disabled: boolean }[]>([]);
    const [primaryCourseUuid, setPrimaryCourseUuid] = useState<string>('');
    const [isPrimaryCourseSelectEnabled, setIsPrimaryCourseSelectEnabled] = useState<boolean>(false);
    const [defaultSelect, setDefaultSelect] = useState<boolean>(true);
    const userUuid = useSelector(selectUserUuid);
    const token = useSelector(selectToken);
    const queryClient = useQueryClient();
    const dispatch = useDispatch();

    const generalError = translate('Something went wrong, please try again later');
    const primaryCourseUpdated = translate('primaryCourseUpdated');
    const courseAlreadyAssigned = translate('courseAlreadyAssigned');

    const { data } = useGetAccountData(userUuid);
    const accountData = data?.account_data;

    const { data: assignedContentData } = useAssignedContent(token);

    const getAdditionalAssignedCourses = (courses?: any[]): string[] => {
        if (!courses || !courses.length) return [];

        const additionalAssigned = courses.filter(course => course.assignedToMe && !course.isPrimary);
        return additionalAssigned.map(course => course.uuid);
    };

    useEffect(() => {
        if (accountData && assignedContentData) {
            setIsPrimaryCourseSelectEnabled(
                accountData.primary_course_update_enabled || accountData.primary_course === null
            );

            if (accountData.courses_list) {
                const additionalAssigned = getAdditionalAssignedCourses(assignedContentData.courses);

                setCoursesList(
                    accountData.courses_list.map(course => ({
                        displayName: course.display_name,
                        uuid: course.uuid,
                        disabled: additionalAssigned.includes(course.uuid),
                        tooltip: additionalAssigned.includes(course.uuid) ? courseAlreadyAssigned : undefined
                    }))
                );
            }

            if (accountData.primary_course) {
                setPrimaryCourseUuid(accountData.primary_course);
            }

            onLoad?.();
        }
    }, [accountData, assignedContentData]);

    const showError = (error: string) => {
        onError?.();

        dispatch(
            showAlert({
                value: error,
                status: 'error'
            })
        );
    };

    const changeMyPrimaryCourse = async (input: {
        courseUuid: string;
    }): Promise<{ success: boolean; error?: string }> => {
        const result = await services.changeMyPrimaryCourse(token, input);
        return result;
    };

    const { mutate } = useMutation(changeMyPrimaryCourse, {
        onSuccess: (result: { success: boolean; error?: string }, variables) => {
            if (result.success) {
                queryClient.setQueryData([getAccountData, userUuid], (oldData: any) => ({
                    account_data: {
                        ...(oldData as platform.AccountData).account_data,
                        primary_course: variables.courseUuid
                    }
                }));
                queryClient.invalidateQueries([getPrimaryCourseData, userUuid]);
                queryClient.invalidateQueries([getPrimaryCourseMetadata, userUuid]);
                queryClient.invalidateQueries([getAssignedContentData, userUuid]);
                queryClient.invalidateQueries([getHomepageCompletionData, userUuid]);
                queryClient.invalidateQueries([getLearnData, token]);
                setNotificationMessage?.(primaryCourseUpdated);
                setShowSuccessNotification?.(true);
                return;
            }
            showError(result?.error || generalError);
        },
        onError: () => {
            showError(generalError);
        }
    });

    useEffect(() => {
        if (shouldSubmitChange === true && primaryCourseUuid !== '') {
            mutate({
                courseUuid: primaryCourseUuid
            });
        }
    }, [mutate, primaryCourseUuid, shouldSubmitChange]);

    return (
        <SelectPrimaryCourse
            isPrimaryCourseSelectEnabled={isPrimaryCourseSelectEnabled}
            primaryCourseUuid={primaryCourseUuid}
            coursesList={coursesList}
            setSelectedUuid={(uuid: string) => {
                onSelect?.(uuid);
                setDefaultSelect(false);

                if (shouldSubmitChange === undefined || shouldSubmitChange === true) {
                    mutate({
                        courseUuid: uuid
                    });
                } else {
                    setPrimaryCourseUuid(uuid);
                }
            }}
            primaryCourseDisplayName={accountData?.primary_course_display_name}
            setPrimaryCourseUuid={setPrimaryCourseUuid}
            defaultSelect={defaultSelect}
        />
    );
};
export interface SelectPrimaryCourseContainerProps {
    setShowSuccessNotification?: Function;
    setNotificationMessage?: Function;
    shouldSubmitChange?: boolean;
    onLoad?: Function;
    onSelect?: Function;
    onError?: Function;
}

export default SelectPrimaryCourseContainer;
